import React, { useEffect, useState } from "react";
import { Flex, Text, IconButton, Button, Dialog, TextField } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard, PermissionAlertDialog } from "components";
import {
  useDeactivateTripadvisorReviews,
  useGetTripadvisorLocationMappings,
  useSaveTripadvisorLocationMappings,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";

export function ActiveTripadvisorReviewsIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems } = useLocationContext();
  const { setToastMessage } = useToast();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const [locationMappings, setLocationMappings] = useState({});
  const [allLocations, setAllLocations] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const organisationId = userProfile?.organisation_id;

  const { data: mappingsData, isLoading: isMappingsLoading } = useGetTripadvisorLocationMappings(
    organisationId,
    !!organisationId
  );

  const saveLocationMappings = useSaveTripadvisorLocationMappings();
  const deactivateTripadvisorReviews = useDeactivateTripadvisorReviews();

  const isOwner = userProfile.user_role === "Owner";

  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);

      const result = await deactivateTripadvisorReviews.mutateAsync({
        organisation_id: organisationId,
      });

      if (result?.data?.status === "success") {
        queryClient.invalidateQueries(["tripadvisor-reviews-status"]);
        queryClient.invalidateQueries(["tripadvisor-location-mappings"]);
        setToastMessage("Tripadvisor reviews integration disconnected successfully");
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Tripadvisor reviews integration");
      }
    } catch (error) {
      console.error("Error deactivating Tripadvisor reviews:", error);
      setToastMessage(error.message || "Failed to disconnect Tripadvisor reviews integration");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  useEffect(() => {
    if (!mappingsData?.data?.mappings || !initialItems) return;

    const mappings = mappingsData.data.mappings;
    const locToUrlPath = {};

    mappings.forEach((mapping) => {
      const locId = mapping.location ?? mapping.location_id;
      if (!locId) return;
      locToUrlPath[String(locId)] = mapping.url_path || "";
    });

    setLocationMappings(locToUrlPath);

    const locations = initialItems.map((loc) => {
      const mapping = mappings.find((m) => String(m.location) === String(loc.value));
      return {
        ...loc,
        isMapped: !!mapping,
        url_path: mapping?.url_path || "",
      };
    });

    setAllLocations(locations);
  }, [mappingsData, initialItems]);

  const handleUrlPathChange = (locationId, value) => {
    setLocationMappings((prev) => ({
      ...(prev || {}),
      [String(locationId)]: value,
    }));
  };

  const handleSaveMappings = async () => {
    try {
      setIsUpdating(true);

      const mappingsToSave = allLocations
        .filter((loc) => !loc.isMapped && locationMappings[String(loc.value)])
        .map((loc) => {
          const urlPath = String(locationMappings[String(loc.value)] || "").trim();
          if (!urlPath) return null;
          return {
            location_id: parseInt(loc.value, 10),
            url_path: urlPath,
          };
        })
        .filter(Boolean);

      if (mappingsToSave.length === 0) {
        setToastMessage("No changes to save");
        return;
      }

      const result = await saveLocationMappings.mutateAsync({
        organisation_id: organisationId,
        mappings: mappingsToSave,
      });

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || result?.data?.error || "Failed to save mappings");
      }

      queryClient.invalidateQueries(["tripadvisor-location-mappings"]);
      queryClient.invalidateQueries(["tripadvisor-reviews-status"]);
      setToastMessage("Mappings saved successfully");
    } catch (error) {
      console.error("Error saving Tripadvisor mappings:", error);
      setToastMessage(error.message || "Failed to save mappings");
    } finally {
      setIsUpdating(false);
    }
  };

  if (isMappingsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Tripadvisor reviews integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Tripadvisor"
        subtitle="Customer reviews"
        image_url="tripadvisor.png"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        <ActiveIntegrateCard.Section
          name="Location Mapping"
          subtitle="Map each b connect location to its corresponding Tripadvisor venue page. Reviews will be synced automatically for mapped locations."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "45%" }}>
                b connect location
              </Text>
              <Text size="2" weight="medium" style={{ width: "45%", paddingLeft: "40px" }}>
                Tripadvisor url_path
              </Text>
            </Flex>

            <Flex direction="column" gap="3">
              {allLocations.length > 0 ? (
                allLocations.map((loc) => (
                  <Flex key={loc.value} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                    <Flex style={{ width: "45%", minWidth: 0 }}>
                      <Flex
                        style={{
                          width: "100%",
                          backgroundColor: "var(--gray-3)",
                          border: "1px solid var(--gray-a7)",
                          borderRadius: "var(--radius-2)",
                          padding: "0 var(--space-2)",
                          height: "34px",
                          alignItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Text
                          size="2"
                          title={loc.label}
                          style={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            width: "100%",
                            color: "var(--gray-a11)",
                          }}
                        >
                          {loc.label}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "45%", minWidth: 0 }}>
                      {loc.isMapped ? (
                        <Flex
                          style={{
                            width: "100%",
                            backgroundColor: "var(--gray-3)",
                            border: "1px solid var(--gray-a7)",
                            borderRadius: "var(--radius-2)",
                            padding: "0 var(--space-2)",
                            height: "34px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Text
                            size="2"
                            title={loc.url_path}
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "var(--gray-a11)",
                            }}
                          >
                            {loc.url_path}
                          </Text>
                        </Flex>
                      ) : isOwner ? (
                        <TextField.Root
                          value={locationMappings[String(loc.value)] || ""}
                          onChange={(e) => handleUrlPathChange(loc.value, e.target.value)}
                          placeholder="Paste TripAdvisor URL or url_path"
                          style={{ width: "100%" }}
                        />
                      ) : (
                        <Flex
                          style={{
                            width: "100%",
                            backgroundColor: "var(--gray-3)",
                            border: "1px solid var(--gray-a7)",
                            borderRadius: "var(--radius-2)",
                            padding: "0 var(--space-2)",
                            height: "34px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Text
                            size="2"
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "var(--gray-a11)",
                            }}
                          >
                            Not mapped
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                ))
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No locations found.</Text>
                </Flex>
              )}
            </Flex>

            <Flex justify="end" style={{ marginTop: "var(--space-2)", padding: "var(--space-2)" }}>
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSaveMappings}
                loading={isUpdating}
                disabled={!isOwner || isUpdating || !allLocations.some((loc) => !loc.isMapped && locationMappings[String(loc.value)])}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Tripadvisor</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This will remove all Tripadvisor location mappings and delete all synced Tripadvisor reviews and sync logs.
          </Dialog.Description>

          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
